<template>
  <div v-if="isMinorMode">
    <!-- 右下角倒计时 -->
    <div class="reading-timer" :class="{ warning: remaining <= 300 }">
      <span class="timer-icon">⏱</span>
      <span class="timer-text">{{ formattedRemaining }}</span>
    </div>

    <!-- 时间耗尽锁死弹窗 -->
    <div v-if="timeUp" class="lock-overlay">
      <div class="lock-dialog">
        <div class="lock-icon">🛡</div>
        <h2>青少年保护模式</h2>
        <p>今日阅读时长已用完（2小时）</p>
        <p class="lock-sub">为了您的健康，请明天再来</p>
      </div>
    </div>
  </div>
</template>

<script>
const STORAGE_PREFIX = 'reading_time_'
const DAILY_LIMIT = 2 * 60 * 60 // 2 小时 = 7200 秒

export default {
  name: 'ReadingTimer',
  data() {
    return {
      elapsed: 0,
      timeUp: false,
      ticker: null,
      // localStorage 不是响应式数据，这里改用 data 保存，保证状态变化能触发视图更新
      userInfo: null
    }
  },
  computed: {
    userId() {
      return this.userInfo?.id || null
    },
    isMinorMode() {
      return Number(this.userInfo?.minor_mode) === 1
    },
    storageKey() {
      return `${STORAGE_PREFIX}${this.userId}`
    },
    remaining() {
      const r = DAILY_LIMIT - this.elapsed
      return r < 0 ? 0 : r
    },
    formattedRemaining() {
      const h = Math.floor(this.remaining / 3600)
      const m = Math.floor((this.remaining % 3600) / 60)
      const s = this.remaining % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    }
  },
  watch: {
    isMinorMode(val) {
      if (val) {
        this.start()
      } else {
        this.stop()
      }
    },
    userId() {
      // 用户切换时重新加载
      if (this.isMinorMode) {
        this.stop()
        this.start()
      }
    }
  },
  methods: {
    // 从 localStorage 同步 userInfo 到响应式数据
    syncUserInfo() {
      try {
        const raw = localStorage.getItem('userinfo')
        this.userInfo = raw ? JSON.parse(raw) : null
      } catch {
        this.userInfo = null
      }
    },
    getToday() {
      return new Date().toISOString().split('T')[0] // 'YYYY-MM-DD'
    },
    loadFromStorage() {
      try {
        const raw = localStorage.getItem(this.storageKey)
        if (!raw) return 0
        const data = JSON.parse(raw)
        if (data.date === this.getToday()) {
          return data.elapsed || 0
        }
        // 跨天了，清零
        return 0
      } catch {
        return 0
      }
    },
    saveToStorage() {
      if (!this.storageKey) return
      localStorage.setItem(this.storageKey, JSON.stringify({
        date: this.getToday(),
        elapsed: this.elapsed
      }))
    },
    start() {
      if (!this.isMinorMode || !this.userId) return
      this.elapsed = this.loadFromStorage()
      if (this.elapsed >= DAILY_LIMIT) {
        this.timeUp = true
        return
      }
      this.timeUp = false
      this.startTick()
    },
    stop() {
      if (this.ticker) {
        clearInterval(this.ticker)
        this.ticker = null
      }
      // 保存当前进度
      this.saveToStorage()
    },
    startTick() {
      if (this.ticker) return
      this.ticker = setInterval(() => {
        // 跨天检测
        const today = this.getToday()
        const raw = localStorage.getItem(this.storageKey)
        if (raw) {
          try {
            const data = JSON.parse(raw)
            if (data.date !== today) {
              this.elapsed = 0
            }
          } catch { /* ignore */ }
        }

        this.elapsed++
        this.saveToStorage()

        if (this.elapsed >= DAILY_LIMIT) {
          this.elapsed = DAILY_LIMIT
          this.saveToStorage()
          this.timeUp = true
          this.stopTick()
        }
      }, 1000)
    },
    stopTick() {
      if (this.ticker) {
        clearInterval(this.ticker)
        this.ticker = null
      }
    },
    // userinfo 变化时（其他组件切换了青少年模式）重新判断计时状态
    onUserInfoUpdated() {
      this.syncUserInfo()
      if (this.isMinorMode) {
        this.stopTick()
        this.start()
      } else {
        this.stop()
        this.timeUp = false
        this.elapsed = 0
      }
    }
  },
  mounted() {
    this.syncUserInfo()
    if (this.isMinorMode) {
      this.start()
    }
    // 监听 userinfo 变化（其他组件可能切换青少年模式）
    window.addEventListener('userinfo-updated', this.onUserInfoUpdated)
  },
  beforeUnmount() {
    this.stop()
    window.removeEventListener('userinfo-updated', this.onUserInfoUpdated)
  }
}
</script>

<style scoped>
/* 右下角倒计时 */
.reading-timer {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9998;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  backdrop-filter: blur(6px);
  transition: background 0.3s;
}
.reading-timer.warning {
  background: rgba(216, 30, 6, 0.9);
  animation: pulse 1s infinite;
}
.timer-icon {
  font-size: 16px;
}
.timer-text {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

/* 锁死弹窗 */
.lock-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}
.lock-dialog {
  background: #fff;
  border-radius: 16px;
  padding: 50px 60px;
  text-align: center;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}
.lock-icon {
  font-size: 56px;
  margin-bottom: 16px;
}
.lock-dialog h2 {
  font-size: 22px;
  color: #333;
  margin-bottom: 12px;
}
.lock-dialog p {
  font-size: 15px;
  color: #666;
  line-height: 1.8;
}
.lock-sub {
  color: #999 !important;
  font-size: 13px !important;
  margin-top: 8px;
}
</style>
